import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import type { UserJob } from '../types';
import { SUBCOMPONENTS } from '../types';

interface UseAutoFinalGenerationParams {
  jobs: UserJob[];
  loadJobs: (isBackgroundRefresh?: boolean) => Promise<void>;
}

interface UseAutoFinalGenerationResult {
  autoFinalJobIds: Set<string>;
  setAutoFinalJobIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleGenerateResume: (jobid: string) => void;
}

/**
 * Section states that can no longer change on their own. Anything else
 * ('generating', 'locked', 'ready', …) means the section is still in flight.
 */
const TERMINAL_SECTION_STATES = new Set(['complete', 'error']);

/** True iff every non-omit section has reached 'complete'. */
export function areAllSectionsComplete(job: UserJob): boolean {
  return SUBCOMPONENTS.every((comp) => {
    const type = job[`type${comp}` as keyof UserJob] as string;
    if (type === 'omit') return true;
    const state = job[`state${comp}` as keyof UserJob] as string;
    return state === 'complete';
  });
}

/**
 * True iff this snapshot is positive evidence that the job is genuinely mid
 * generation — the card says 'Generating', or at least one non-omit section is
 * in a non-terminal state.
 */
export function isJobInFlight(job: UserJob): boolean {
  if (job.jobphase === 'Generating') return true;
  return SUBCOMPONENTS.some((comp) => {
    const type = job[`type${comp}` as keyof UserJob] as string;
    if (type === 'omit') return false;
    const state = job[`state${comp}` as keyof UserJob] as string;
    return !TERMINAL_SECTION_STATES.has(state);
  });
}

/**
 * Decide, for ONE jobs snapshot, whether the auto-final safety net may fire.
 *
 * WQ 40c9de7b: the old rule was "tracked + all sections complete", which fired
 * against STALE data. Immediately after the user clicks Generate, the client's
 * `jobs` array still holds the PREVIOUS generation's all-'complete' states (the
 * backend has not flipped them to 'generating' yet), so the hook called
 * generateFinalFiles at once and published an INCOMPLETE resume — racing the
 * generation it had just started.
 *
 * The fix is a per-job "armed" latch: a job only becomes eligible once a
 * snapshot has shown it actually IN FLIGHT. Firing additionally requires the
 * latch to have been set by an EARLIER snapshot (`armedBefore`), so a single
 * snapshot can never both arm and fire — that closes the remaining window where
 * a card already sitting in 'Generating' would arm off stale all-complete
 * section states. A real generation spans many poll cycles, so nothing is lost;
 * and the authoritative backend trigger
 * (subcomponent._maybe_advance_pipeline) renders the moment the last
 * file-blocking section completes. This hook is only the client-side safety net
 * for when that backend dispatch fails.
 */
export function evaluateAutoFinal(
  job: UserJob | undefined,
  armedBefore: boolean,
): { armed: boolean; fire: boolean } {
  if (!job) return { armed: armedBefore, fire: false };
  const armed = armedBefore || isJobInFlight(job);
  return { armed, fire: armedBefore && areAllSectionsComplete(job) };
}

export default function useAutoFinalGeneration({
  jobs,
  loadJobs,
}: UseAutoFinalGenerationParams): UseAutoFinalGenerationResult {
  const [autoFinalJobIds, setAutoFinalJobIds] = useState<Set<string>>(new Set());
  const autoFinalTriggered = useRef<Set<string>>(new Set());
  // Jobs we have OBSERVED enter generation — see evaluateAutoFinal().
  const autoFinalArmed = useRef<Set<string>>(new Set());

  // Auto-final-generation: when all non-omit sections complete for a tracked job, trigger final files
  useEffect(() => {
    if (autoFinalJobIds.size === 0) return;

    autoFinalJobIds.forEach((jobid) => {
      // Don't trigger twice
      if (autoFinalTriggered.current.has(jobid)) return;

      const job = jobs.find((j) => j.jobid === jobid);
      if (!job) return;

      const { armed, fire } = evaluateAutoFinal(job, autoFinalArmed.current.has(jobid));
      if (armed) autoFinalArmed.current.add(jobid);

      if (fire) {
        autoFinalTriggered.current.add(jobid);
        console.log(`Auto-generating final files for job ${jobid}`);
        api.generateFinalFiles(jobid).then(() => {
          loadJobs(true);
          // Clean up tracking after a delay
          setTimeout(() => {
            setAutoFinalJobIds((prev) => {
              const next = new Set(prev);
              next.delete(jobid);
              return next;
            });
            autoFinalTriggered.current.delete(jobid);
            // Disarm too: re-firing now requires observing a NEW generation.
            autoFinalArmed.current.delete(jobid);
          }, 5000);
        }).catch((err) => {
          console.error(`Failed to auto-generate final files for ${jobid}:`, err);
          // Stay armed so a retry on the next snapshot is still eligible.
          autoFinalTriggered.current.delete(jobid);
        });
      }
    });
  }, [jobs, autoFinalJobIds, loadJobs]);

  // Handle "Generate Resume" button from JobCard - start tracking for auto-final
  const handleGenerateResume = useCallback((jobid: string) => {
    // A fresh Generate click must start UNARMED: the jobs snapshot we hold right
    // now still describes the previous generation.
    autoFinalArmed.current.delete(jobid);
    autoFinalTriggered.current.delete(jobid);
    setAutoFinalJobIds((prev) => new Set(prev).add(jobid));
  }, []);

  return { autoFinalJobIds, setAutoFinalJobIds, handleGenerateResume };
}
